import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, Bot, AlertTriangle, Download, ChevronLeft, ChevronRight, Search, Trash2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAFETY_COLORS = {
  crisis: "bg-red-50 text-red-700 border-red-200",
  medication: "bg-amber-50 text-amber-700 border-amber-200",
  diagnosis: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pricing: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminAssistantLogsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [safety, setSafety] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [drawer, setDrawer] = useState(null); // session_id
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/assistant-logs`, {
        params: {
          page,
          per_page: 30,
          q: q || undefined,
          safety: safety || undefined,
        },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load assistant logs");
    } finally {
      setLoading(false);
    }
  }, [page, q, safety]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (sessionId) => {
    setDrawer(sessionId);
    setDrawerData(null);
    setDrawerLoading(true);
    try {
      const res = await axios.get(`${API}/admin/assistant-logs/${sessionId}`);
      setDrawerData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load conversation");
      setDrawer(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const toggleRow = (sessionId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.session_id)));
  };

  const onBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} conversation(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/assistant-logs/bulk-delete`, {
        session_ids: Array.from(selected),
      });
      toast.success(`Deleted ${res.data.deleted_sessions} session(s), ${res.data.deleted_messages} message(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/exports/assistant-logs.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "psytech_assistant_logs.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-assistant-logs">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bot className="w-5 h-5" /> AI Assistant Logs
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total conversation{total === 1 ? "" : "s"}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search messages…"
              className="text-sm rounded-lg border border-slate-200 pl-8 pr-3 py-2 bg-white w-56"
              data-testid="assistant-logs-search"
            />
          </div>
          <select
            value={safety}
            onChange={(e) => { setPage(1); setSafety(e.target.value); }}
            className="text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white"
            data-testid="assistant-logs-safety-filter"
          >
            <option value="">All safety flags</option>
            <option value="any">Any flag (non-empty)</option>
            <option value="crisis">crisis</option>
            <option value="medication">medication</option>
            <option value="diagnosis">diagnosis</option>
            <option value="pricing">pricing</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={onBulkDelete}
            disabled={selected.size === 0 || deleting}
            data-testid="assistant-logs-bulk-delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            Delete{selected.size ? ` (${selected.size})` : ""}
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="assistant-logs-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Bot className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No conversations match these filters yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5 w-10">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selected.size === items.length}
                      onChange={toggleAll}
                      data-testid="assistant-logs-select-all"
                    />
                  </th>
                  <th className="px-3 py-2.5 font-semibold">Last activity</th>
                  <th className="px-3 py-2.5 font-semibold">Session</th>
                  <th className="px-3 py-2.5 font-semibold">Msgs</th>
                  <th className="px-3 py-2.5 font-semibold">Safety</th>
                  <th className="px-3 py-2.5 font-semibold">Last message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr
                    key={it.session_id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => openDrawer(it.session_id)}
                    data-testid={`assistant-log-row-${it.session_id}`}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(it.session_id)}
                        onChange={() => toggleRow(it.session_id)}
                        data-testid={`assistant-log-check-${it.session_id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                      {new Date(it.last_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-slate-500" title={it.session_id}>
                      {it.session_id.slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 tabular-nums">
                      {it.message_count}
                      <span className="text-slate-400 text-xs"> ({it.user_messages}u)</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {it.safety_flags.length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          it.safety_flags.map((f) => (
                            <span
                              key={f}
                              className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border ${SAFETY_COLORS[f] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                            >
                              {f === "crisis" && <AlertTriangle className="w-2.5 h-2.5" />}
                              {f}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 max-w-md">
                      <div className="truncate" title={it.last_user_msg}>
                        {it.last_user_msg || "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <div className="text-slate-500">Page {page} of {totalPages}</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex" data-testid="assistant-log-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setDrawer(null)} />
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">Conversation</div>
                <div className="text-sm font-mono text-slate-800">{drawer}</div>
              </div>
              <button
                onClick={() => setDrawer(null)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
                data-testid="assistant-log-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {drawerLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : !drawerData ? (
                <div className="text-slate-500 text-sm">No data</div>
              ) : (
                drawerData.messages.map((m, idx) => (
                  <div
                    key={idx}
                    className={`rounded-xl p-3 text-sm border ${
                      m.role === "user"
                        ? "bg-cyan-50 border-cyan-100 ml-8"
                        : "bg-slate-50 border-slate-200 mr-8"
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="uppercase tracking-wider font-semibold">{m.role}</span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <div className="whitespace-pre-wrap text-slate-800">{m.content}</div>
                    {m.meta?.safety_triggered && (
                      <div className="mt-2">
                        <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-md border ${SAFETY_COLORS[m.meta.safety_triggered] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                          safety: {m.meta.safety_triggered}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
